import dayjs from 'dayjs'
import { customViewTemplateType, quoteLineType } from '../../types'
import { PRODUCT_CATEGORY_SOURCE_LABELS, productCategoryOptions } from './productCategories'

export type ColumnFormat = 'currency' | 'percentage' | 'number' | 'date' | 'boolean' | 'string' | 'autocomplete'

type ColumnConfigBase = {
  format?: ColumnFormat
  size?: number
  nonEdit?: boolean
  options?: { label: string; value: string | number | boolean | null }[]
  label?: string
  masterList?: 'manufacturer' | 'supplier'
  labelFallback?: (item: quoteLineType) => string | null | undefined
}

export type ColumnConfig<T> =
  | (ColumnConfigBase & {
      property: keyof T
      compute?: never
    })
  | (ColumnConfigBase & {
      compute: (item: T) => number | string | null
    })

export const COLUMN_MAP: Record<string, ColumnConfig<quoteLineType>> = {
  '#': { property: 'LineNumber', format: 'number', nonEdit: true },
  Description: { property: 'Description' },
  Qty: { property: 'Qty', format: 'number' },
  Provider: { property: 'Provider', label: 'Supplier', masterList: 'supplier' },
  Manufacturer: {
    property: 'Manufacturer',
    format: 'autocomplete',
    masterList: 'manufacturer',
  },
  'Unit Cost': { property: 'UnitCost', format: 'currency' },
  'Ext Cost': { property: 'ExtCost', format: 'currency', nonEdit: true },
  'Gross Profit (%)': { property: 'Margin', format: 'percentage' },
  'Gross Profit ($)': {
    format: 'currency',
    compute: item => (item.ExtPrice ?? 0) - (item.ExtCost ?? 0) - (item.TotalFees ?? 0),
  },
  'Markup (%)': {
    format: 'percentage',
    nonEdit: true,
    compute: item => ((item.ExtPrice ?? 0) - (item.ExtCost ?? 0)) / (item.ExtCost ?? 0),
  },
  'Markup ($)': {
    format: 'currency',
    nonEdit: true,
    compute: item => (item.ExtPrice ?? 0) - (item.ExtCost ?? 0),
  },
  'Ext Price': { property: 'ExtPrice', format: 'currency', nonEdit: true },
  'Contract Ceiling Price': { property: 'ContractCeilingPrice', format: 'currency' },
  'Unit Price': { property: 'UnitPrice', format: 'currency' },
  'Credit Card Fee': { property: 'CreditCardFee', format: 'currency', nonEdit: true },
  'Contract Fees': { property: 'ContractFees', format: 'currency', nonEdit: true },
  'Partner Fees': { property: 'PartnerFees', format: 'currency', nonEdit: true },
  'Total Fees': { property: 'TotalFees', format: 'currency', nonEdit: true },
  'Contract Catalog': { property: 'ContractCatalog' },
  'Mfr Part Number': { property: 'MfrPartNumber' },
  'Display Part Number': { property: 'DisplayPartNumber' },
  'Supplier Part Number': { property: 'VendorPartNumber' },
  MSRP: { property: 'MSRP', format: 'currency' },
  'OEM Code': { property: 'OEMCode', nonEdit: true },
  CLIN: { property: 'CLIN', nonEdit: true },
  'Product Type': {
    property: 'ProductType',
    options: [
      { label: 'Product', value: 'Product' },
      { label: 'Service', value: 'Service' },
      { label: 'Maintenance', value: 'Maintenance' },
      { label: 'Labor Rate', value: 'Labor Rate' },
    ],
  },
  'Product Category': {
    property: 'ProductCategory',
    get options() {
      return productCategoryOptions()
    },
    labelFallback: item =>
      item.ProductCategory ??
      (item.ProductCategorySource === 'PENDING_AI' ? PRODUCT_CATEGORY_SOURCE_LABELS.PENDING_AI : null),
  },
  'Start Date': { property: 'StartDate', format: 'date' },
  'End Date': { property: 'EndDate', format: 'date' },
  'Service Tag': { property: 'ServiceTag' },
  'License SN': { property: 'LicenseSN' },
  'Unit Of Measure': { property: 'UnitOfMeasure' },
  TAA: {
    property: 'TAA',
    options: [
      { label: 'C | Compliant', value: 'C' },
      { label: 'NA | Not Applicable', value: 'NA' },
      { label: 'NC | Not Compliant', value: 'NC' },
    ],
  },
  'Country Of Origin': { property: 'CountryOfOrigin' },
  UPC: { property: 'UPC' },
  UNSPSC: { property: 'UNSPSC' },
  'Energy Star': {
    property: 'EnergyStar',
    options: [
      { label: 'C | Compliant', value: 'C' },
      { label: 'NA | Not Applicable', value: 'NA' },
      { label: 'NC | Not Compliant', value: 'NC' },
    ],
  },
  EPEAT: {
    property: 'EPEAT',
    options: [
      { label: 'B | EPEAT Certified Bronze', value: 'B' },
      { label: 'G | EPEAT Certified Gold', value: 'G' },
      { label: 'S | EPEAT Certified Silver', value: 'S' },
      { label: 'NA | Not Applicable', value: 'NA' },
      { label: 'NC | Non-Compliant', value: 'NC' },
    ],
  },
  Notes: { property: 'Notes' },
}

export const DEFAULT_TEMPLATE: customViewTemplateType = {
  TemplateName: 'Default',
  Columns: [
    { Name: '#', Width: 1, Order: 1 },
    { Name: 'Provider', Width: 2.5, Order: 2 },
    { Name: 'Supplier Part Number', Width: 1.5, Order: 3 },
    { Name: 'Manufacturer', Width: 2.5, Order: 4 },
    { Name: 'OEM Code', Width: 1, Order: 5 },
    { Name: 'Mfr Part Number', Width: 1.5, Order: 6 },
    { Name: 'Qty', Width: 1, Order: 7 },
    { Name: 'Ext Cost', Width: 1, Order: 8 },
    { Name: 'Unit Cost', Width: 1, Order: 9 },
    { Name: 'Ext Price', Width: 1, Order: 10 },
    { Name: 'Contract Ceiling Price', Width: 1.5, Order: 11 },
    { Name: 'Contract Catalog', Width: 2, Order: 12 },
    { Name: 'Product Category', Width: 2, Order: 13 },
  ],
}

export const getColumnWidth = (relativeWidth: number, totalWidth: number): string => {
  return `${(relativeWidth / (totalWidth || 1)) * 100}%`
}

export function getColumnValue(
  item: quoteLineType,
  columnName: string,
  editing: true,
): quoteLineType[keyof quoteLineType]
export function getColumnValue(item: quoteLineType, columnName: string, editing?: false): string
export function getColumnValue(
  item: quoteLineType,
  columnName: string,
  editing?: boolean,
): quoteLineType[keyof quoteLineType] | string {
  const columnConfig = COLUMN_MAP[columnName]
  const value = columnConfig.compute ? columnConfig.compute(item) : item[columnConfig.property]

  if (editing) {
    return value
  }

  if (columnConfig.options) {
    const option = columnConfig.options.find(o => o.value === value || o.value === Number(value))
    return option ? option.label : columnConfig.labelFallback?.(item) ?? '-'
  }

  if (value === null) {
    return '-'
  }

  switch (columnConfig.format) {
    case 'currency':
      return typeof value === 'number'
        ? value.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
        : String(value)
    case 'percentage':
      return typeof value === 'number' ? `${(value * 100).toFixed(2)}%` : String(value)
    case 'number':
      return typeof value === 'number' ? value.toLocaleString('en-US') : String(value)
    case 'date':
      return typeof value === 'string' || value instanceof Date
        ? dayjs(value).utc().format('MM/DD/YYYY')
        : 'Invalid date'
    case 'boolean':
      return value ? 'Yes' : 'No'
    default:
      return String(value)
  }
}
